import React, { Suspense, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@/features/auth';
import { useGetObjects } from '@/features/objects/api/use-get-objects';
import { getAllowedContractIdsFromPermissions } from '@/helpers/get-allowed-contract-ids-from-permissions';
import { useFilterParams } from '@/hooks/use-filter-params';
import { useLocale } from '@/hooks/use-locale';
import { useUserInfoStore } from '@/store';

import './ObjectsMap.css';

import { CustomerContractRole } from '@ekt-group/general-purpose-api-interfaces/src/types/customer-contract-role.enum';
import { ObjectsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/modules/objects/types/objects-sideloading.enum';
import { ObjectEntity } from '@ekt-group/general-purpose-api-interfaces';
import { Maximize02 } from '@untitled-ui/icons-react';
import { MapComponent, MapMode, MapTileMode } from '@ekt-group/map-component';
import { Skeleton } from 'antd';

const ObjectsMap = () => {
  const { currentLocale } = useLocale();
  const { data: user } = useUser();
  const { selectedAccount } = useUserInfoStore();
  const { t } = useTranslation('desktopPage');

  const { filters, setFilterValue } = useFilterParams(['objectAddressId']);

  const objectsQuery = useGetObjects({
    params: {
      contractIds:
        user?.roles?.includes(CustomerContractRole.ChildContract) || user?.roles?.includes(CustomerContractRole.SharedObjectContract)
          ? getAllowedContractIdsFromPermissions(selectedAccount?.permissions)
          : undefined,
      sideloading: [ObjectsSideloading.ObjectAddress],
      ownerCodes: [selectedAccount?.code],
      isActive: true,
      isDeleted: false,
      disablePagination: true,
    },
    queryConfig: {
      enabled: !!selectedAccount?.code,
    },
  });

  const objectsData = objectsQuery.data as ObjectEntity[];

  const objectId = objectsData?.find((object) => object.objectAddress?.id === Number(filters.objectAddressId))?.id;
  const objectIdRef = useRef(objectId);

  useEffect(() => {
    objectIdRef.current = objectId;
  }, [objectId]);

  const handleObjectClick = (objectAddressId: number) => {
    const object = objectsData?.find((object) => object.objectAddress?.id === objectAddressId);
    setFilterValue('objectAddressId', object.id === objectIdRef.current ? null : objectAddressId?.toString());
  };

  return (
    <div className="w-full h-full relative flex-1">
      {objectsQuery.isLoading || objectsQuery.isFetching ? (
        <Skeleton.Node active className="absolute inset-0 w-full h-full z-10 bg-white/60 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full border-4 border-gray-300 border-t-transparent animate-spin" />
        </Skeleton.Node>
      ) : null}
      <Suspense fallback={null}>
        {objectsQuery.isSuccess && (
          <MapComponent
            tilesMode={MapTileMode.OpenStreetMaps}
            selectedObjectId={objectId}
            mode={MapMode.SelfServiceObjects}
            locale={currentLocale}
            onObjectClick={handleObjectClick}
            objects={objectsData}
            generalPurposesHostUrl={import.meta.env.VITE_GENERAL_PURPOSE_HOST_URL}
          />
        )}
      </Suspense>

      <button className="w-[148px] hidden h-[36px] items-center justify-center px-[22.5px] py-2 gap-1 rounded-lg bg-[#F9FAE0] absolute bottom-10 right-5">
        <Maximize02 className="w-5 h-5 text-gray-10" />
        <span className="text-base leading-5 font-semibold text-gray-10">{t('openMap')}</span>
      </button>
    </div>
  );
};

export default ObjectsMap;
